import { Link } from '@tanstack/react-router';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { getButtonStyles } from '@sc/shared-ui';
import { ReactElement, ReactNode } from 'react';
import env from '../environment';
import { ResponsiveImage } from './ResponsiveImage';

interface Props {
  title?: ReactNode | undefined;
  body?: string | undefined;
  primaryLabel?: string;
  primaryTo?: string;
  primaryExternal?: boolean;
  secondaryTo?: string | undefined;
  secondaryLabel?: string | undefined;
}

const defaultTitle = (
  <>
    Stark Crawl — web crawling for market research
  </>
);

const defaultBody =
  'Discover hosts, crawl pages, extract structured fields, and capture prospects — turn the open web into research-ready data without building your own scrapers.';

export const BottomCTA = ({
  title,
  body,
  primaryLabel,
  primaryTo,
  primaryExternal,
  secondaryTo,
  secondaryLabel,
}: Props): ReactElement => {
  const resolvedTitle = title ?? defaultTitle;
  const resolvedBody = body ?? defaultBody;
  const resolvedPrimaryLabel = primaryLabel ?? 'Try for free';
  const isExternalPrimary = primaryExternal ?? !primaryTo;
  const resolvedPrimaryHref = primaryTo ?? env.appUrl + '/register';
  const showSecondary = Boolean(secondaryTo && secondaryLabel);
  const isExternalSecondary =
    showSecondary &&
    typeof secondaryTo === 'string' &&
    /^https?:\/\//.test(secondaryTo);

  const primaryButton = isExternalPrimary ? (
    <a
      href={resolvedPrimaryHref}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={`${resolvedPrimaryLabel} (opens in new tab)`}
      {...getButtonStyles({
        type: 'primary',
        size: 'xl',
        additionalClassnames: 'gap-x-2',
      })}
    >
      {resolvedPrimaryLabel}
      <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
    </a>
  ) : (
    <Link
      to={resolvedPrimaryHref}
      aria-label={resolvedPrimaryLabel}
      {...getButtonStyles({
        type: 'primary',
        size: 'xl',
        additionalClassnames: 'gap-x-2',
      })}
    >
      {resolvedPrimaryLabel}
      <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
    </Link>
  );

  return (
    <section className="py-25 px-5">
      <div className="max-w-315 mx-auto">
        <div className="bg-background-tertiary rounded-md overflow-hidden flex flex-col-reverse lg:flex-row items-center gap-10 lg:gap-x-5 lg:gap-y-0 p-10">
          <div className="flex flex-col gap-5 flex-1">
            <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
              {resolvedTitle}
            </h2>
            <p className="text-lg leading-6.5 text-grey-200">
              {resolvedBody}
            </p>
            <div className="flex items-center gap-3 flex-wrap mt-5">
              {primaryButton}
              {showSecondary &&
                (isExternalSecondary ? (
                  <a
                    href={secondaryTo}
                    target="_blank"
                    rel="noreferrer noopener"
                    aria-label={`${secondaryLabel} (opens in new tab)`}
                    {...getButtonStyles({
                      type: 'basic',
                      size: 'xl',
                    })}
                  >
                    {secondaryLabel}
                  </a>
                ) : (
                  <Link
                    to={secondaryTo ?? '/'}
                    {...getButtonStyles({
                      type: 'basic',
                      size: 'xl',
                    })}
                  >
                    {secondaryLabel}
                  </Link>
                ))}
            </div>
          </div>
          <div className="relative overflow-hidden w-full lg:w-auto lg:-mr-30 border-8 border-grey-700 rounded-lg">
            <ResponsiveImage
              src="/images/hero.png"
              alt="Stark Crawl interface"
              className="w-full h-auto lg:h-103.5 lg:w-auto"
              sizes="(max-width: 1024px) 100vw, 694px"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
